import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * National Health Intelligence dashboard.
 *
 * Platform-admin only. Aggregates Value & Impact across every active
 * subscribing organization. Designed for Ministry-of-Health style
 * conversations and funder pitches that need to extend the per-org
 * story to a national rollup.
 *
 * Privacy: per-org rows are visible to platform admins only (the
 * endpoint is gated server-side by IsAdminRole). National totals are
 * not patient-identifying and are safe to display in any audience.
 */
export default function NationalDashboard() {
  const [days, setDays] = useState(30)
  const [sortKey, setSortKey] = useState('total_savings')
  const [sortDir, setSortDir] = useState('desc')

  const q = useQuery({
    queryKey: ['national-dashboard', days],
    queryFn: async () => {
      const { data } = await api.get(endpoints.nationalDashboard, {
        params: { days },
      })
      return data
    },
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="National health intelligence"
          subtitle="Aggregate Value & Impact across every ICER customer organization."
          right={
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Window:</label>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="border rounded px-2 py-1 text-xs bg-white"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 180 days</option>
                <option value={365}>Last 365 days</option>
              </select>
            </div>
          }
        />
      </Card>

      {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && (
        <Card>
          <div className="text-sm text-slate-400">Loading national rollup…</div>
        </Card>
      )}

      {q.data && (
        <NationalBody
          data={q.data}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={(key) => {
            if (sortKey === key) setSortDir(sortDir === 'desc' ? 'asc' : 'desc')
            else { setSortKey(key); setSortDir('desc') }
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
function NationalBody({ data, sortKey, sortDir, onSort }) {
  const t = data.totals || {}
  const contributors = sortContributors(data.contributors || [], sortKey, sortDir)

  return (
    <>
      {/* ---- Executive statement banner ---- */}
      <Card>
        <CardHeader
          title={`Value & impact · last ${data.window_days} days`}
          subtitle="Rolled up across every active organization on the platform."
          right={
            <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-coral-50 text-coral-700 border-coral-200">
              National rollup
            </span>
          }
        />
        {data.statement && (
          <div className="rounded-xl bg-gradient-to-r from-wantok-50 to-coral-50 border border-wantok-100 p-3 text-sm text-slate-800">
            {data.statement}
          </div>
        )}

        {/* ---- Headline tiles ---- */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <Tile label="Organizations" value={t.organizations ?? 0} colour="slate" />
          <Tile label="Avoided visits" value={t.avoided_visits ?? 0} colour="emerald" />
          <Tile label="Total savings" value={`PGK ${fmtPGK(t.total_savings)}`} colour="emerald" />
          <Tile label="Net value" value={`PGK ${fmtPGK(t.net_value)}`} colour="sky" />
          <Tile
            label="ROI"
            value={t.roi_percent != null ? `${fmtNum(t.roi_percent, 0)}%` : '—'}
            colour="rose"
          />
          <Tile label="Hours saved" value={fmtNum(t.time_saved_hours, 0)} colour="amber" />
        </div>

        {/* ---- Cohort & impact line ---- */}
        <div className="mt-3 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
          <span><b>{t.encounters_total ?? 0}</b> encounters in window</span>
          <span><b>{t.telehealth_encounters ?? 0}</b> telehealth</span>
          <span><b>{t.physical_encounters ?? 0}</b> physical</span>
          <span><b>{t.admissions_avoided ?? 0}</b> emergency admissions prevented</span>
          <span><b>{t.active_staff ?? 0}</b> enrolled staff</span>
          <span className="text-slate-400">
            spend used as ROI denominator: PGK {fmtPGK(t.indicative_spend)}
          </span>
        </div>
      </Card>

      {/* ---- Contributors table ---- */}
      <Card>
        <CardHeader
          title="Contributing organizations"
          subtitle="Click any column heading to sort. Rows are visible only to platform admins."
        />
        {contributors.length === 0 ? (
          <div className="text-xs text-slate-400 italic py-4">
            No active organizations enrolled yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <Th onClick={() => onSort('name')} active={sortKey === 'name'} dir={sortDir}>Organization</Th>
                  <Th onClick={() => onSort('org_type')} active={sortKey === 'org_type'} dir={sortDir} align="left">Type</Th>
                  <Th onClick={() => onSort('active_staff')} active={sortKey === 'active_staff'} dir={sortDir} align="right">Active staff</Th>
                  <Th onClick={() => onSort('encounters_total')} active={sortKey === 'encounters_total'} dir={sortDir} align="right">Encounters</Th>
                  <Th onClick={() => onSort('avoided_visits')} active={sortKey === 'avoided_visits'} dir={sortDir} align="right">Avoided visits</Th>
                  <Th onClick={() => onSort('total_savings')} active={sortKey === 'total_savings'} dir={sortDir} align="right">Total savings (PGK)</Th>
                  <Th onClick={() => onSort('roi_percent')} active={sortKey === 'roi_percent'} dir={sortDir} align="right">ROI</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contributors.map((c, i) => (
                  <tr key={c.id ?? i}>
                    <td className="p-2 font-semibold text-slate-900">{c.name}</td>
                    <td className="p-2 text-slate-600 capitalize">{c.org_type}</td>
                    <td className="p-2 text-right tabular-nums">{c.active_staff}</td>
                    <td className="p-2 text-right tabular-nums">{c.encounters_total}</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-wantok-700">{c.avoided_visits}</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{fmtPGK(c.total_savings)}</td>
                    <td className="p-2 text-right tabular-nums font-semibold text-coral-600">
                      {c.roi_percent != null ? `${fmtNum(c.roi_percent, 0)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t">
                <tr className="text-sm">
                  <td className="p-2 font-semibold text-slate-700">National total</td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right font-semibold tabular-nums">{t.active_staff ?? 0}</td>
                  <td className="p-2 text-right font-semibold tabular-nums">{t.encounters_total ?? 0}</td>
                  <td className="p-2 text-right font-bold tabular-nums text-wantok-800">{t.avoided_visits ?? 0}</td>
                  <td className="p-2 text-right font-bold tabular-nums text-emerald-800">PGK {fmtPGK(t.total_savings)}</td>
                  <td className="p-2 text-right font-bold tabular-nums text-coral-700">
                    {t.roi_percent != null ? `${fmtNum(t.roi_percent, 0)}%` : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* ---- Platform Economics (Phase 6.2) — ICER's cost-side P&L ---- */}
      {data.platform_economics && (
        <PlatformEconomicsSection eco={data.platform_economics} />
      )}

      {/* ---- Footer note ---- */}
      <div className="text-[11px] text-slate-500 italic">
        Computed {data.computed_at ? new Date(data.computed_at).toLocaleString() : ''} ·
        Aggregate-only · Platform-admin view · Each per-org figure is itself a
        privacy-floor-protected aggregate (k=5 on every breakdown bucket).
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Platform Economics — ICER Health's cost-side panel.
//
// Lives on the National Dashboard (platform-admin only). Shows revenue,
// operating costs (manual entries + auto-derived clinic-time-foregone),
// net margin, and a value-per-cost efficiency ratio.
//
// Critical: this surface contains ICER's competitive financial info.
// The entire National Dashboard is gated by IsAdminRole server-side, so
// no further FE gating is needed — but never duplicate this card
// anywhere on an org-facing route.
// ---------------------------------------------------------------------------
function PlatformEconomicsSection({ eco }) {
  const costs = eco.costs || {}
  const cats = costs.by_category || []
  const totalCost = costs.total_pgk || 0
  const inputs = eco.inputs || {}

  return (
    <Card>
      <CardHeader
        title={`Platform economics · last ${eco.window_days} days`}
        subtitle="ICER Health's own running costs vs subscription revenue. Platform-admin view only — never visible to org admins."
        right={
          <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 border-slate-300">
            ICER internal
          </span>
        }
      />

      {eco.statement && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800">
          {eco.statement}
        </div>
      )}

      {/* ---- Headline P&L tiles ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
        <Tile
          label="Revenue (subscription)"
          value={`PGK ${fmtPGK(eco.revenue_pgk)}`}
          colour="emerald"
        />
        <Tile
          label="Operating costs"
          value={`PGK ${fmtPGK(eco.costs?.total_pgk)}`}
          colour="rose"
        />
        <Tile
          label="Net margin"
          value={`PGK ${fmtPGK(eco.net_margin_pgk)}`}
          colour={eco.net_margin_pgk >= 0 ? 'sky' : 'rose'}
        />
        <Tile
          label="Margin %"
          value={eco.margin_pct != null ? `${fmtNum(eco.margin_pct, 0)}%` : '—'}
          colour="amber"
        />
        <Tile
          label="Value efficiency"
          value={
            eco.efficiency_ratio != null
              ? `${fmtNum(eco.efficiency_ratio, 2)}x`
              : '—'
          }
          colour="slate"
        />
      </div>

      {/* ---- Cohort line: where the costs came from at a glance ---- */}
      <div className="mt-3 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
        <span>
          <b>{inputs.active_orgs ?? 0}</b> active orgs contribute revenue
        </span>
        <span>
          <b>{inputs.admin_encounter_count ?? 0}</b> admin-handled encounters
        </span>
        <span className="text-slate-400">
          (clinic rate: K{fmtNum(inputs.clinic_hourly_rate_pgk, 0)}/hr · avg
          consult: {inputs.consultation_avg_minutes ?? 15} min)
        </span>
        <span className="text-slate-400">
          value delivered to orgs: PGK {fmtPGK(eco.value_delivered_pgk)}
        </span>
      </div>

      {/* ---- Cost breakdown by category ---- */}
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
          Cost breakdown
        </div>
        {cats.length === 0 ? (
          <div className="text-xs text-slate-400 italic">
            No costs recorded yet. Enter the first cost at{' '}
            <a
              href="/superuser/platform-costs"
              className="underline text-wantok-700 hover:text-wantok-800"
            >
              /superuser/platform-costs
            </a>
            {' '}to start populating this view.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {cats.map((c, i) => {
              const pct = totalCost > 0 ? (c.total_pgk / totalCost) * 100 : 0
              const isAuto = c.source === 'auto'
              return (
                <li key={i} className="text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-slate-700 truncate">
                      {c.category_label}
                      {isAuto && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide border rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-700 border-amber-200 align-middle">
                          auto
                        </span>
                      )}
                      {c.hours != null && (
                        <span className="text-[11px] text-slate-400 ml-2">
                          · {fmtNum(c.hours, 1)} hrs
                        </span>
                      )}
                    </span>
                    <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                      K{fmtPGK(c.total_pgk)}
                      <span className="text-[11px] text-slate-500 ml-2">
                        {fmtNum(pct, 0)}%
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div
                      className={`h-full rounded-full ${isAuto ? 'bg-amber-500' : 'bg-rose-500'}`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* ---- Methodology footnote ---- */}
      <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        <b>How this is calculated:</b> Revenue is the sum of pro-rated annual
        subscriptions across active orgs (annual × {eco.window_days}/365).
        Operating costs are manual entries from{' '}
        <a
          href="/superuser/platform-costs"
          className="underline hover:text-slate-700"
        >
          /superuser/platform-costs
        </a>
        {' '}plus an auto-derived clinic-time-foregone line for each platform-admin
        encounter. Value efficiency is PGK of customer-side value delivered per
        PGK of operating cost.
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
function Tile({ label, value, colour = 'slate' }) {
  const map = {
    slate:   'border-sand-200/60 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    sky:     'border-sky-200 bg-sky-50',
    amber:   'border-amber-200 bg-amber-50',
    rose:    'border-rose-200 bg-rose-50',
  }
  return (
    <div className={`border rounded-2xl px-4 py-3 ${map[colour]}`}>
      <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{value ?? '—'}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
    </div>
  )
}

function Th({ children, onClick, active, dir, align = 'left' }) {
  const arrow = active ? (dir === 'desc' ? ' ▼' : ' ▲') : ''
  return (
    <th
      onClick={onClick}
      className={`p-2 cursor-pointer select-none hover:text-slate-900 ${active ? 'text-slate-900' : ''} text-${align}`}
    >
      {children}{arrow}
    </th>
  )
}

function sortContributors(rows, key, dir) {
  const sign = dir === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    let av = a[key]
    let bv = b[key]
    if (av == null && bv == null) return 0
    if (av == null) return 1   // nulls always last
    if (bv == null) return -1
    if (typeof av === 'string') {
      return sign * av.localeCompare(bv)
    }
    return sign * (av - bv)
  })
}

function fmtPGK(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

function fmtNum(n, dp = 0) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  })
}
